import { apiFetch } from "./apiClient";

const BASE = "/api/services/route-templates";

// The route template editor passes { deferSync: true } on every edit so the
// backend does not sync jobs / rebuild periods after each call; the user then
// runs one sync + rebuild from a chosen date (syncRouteTemplateFutureJobs).
function withDefer(body, options = {}) {
  return options.deferSync ? { ...body, defer_sync: true } : body;
}

async function parseOrThrow(res, fallbackMessage) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || fallbackMessage);
    error.code = data.code || null;
    error.status = res.status;
    throw error;
  }
  return data;
}

export async function getRouteTemplates(filters = {}) {
  const params = new URLSearchParams();
  if (filters.customerId != null) params.set("customer_id", filters.customerId);
  if (filters.locationId != null) params.set("location_id", filters.locationId);
  if (filters.active != null) params.set("active", filters.active);

  const query = params.toString() ? `?${params}` : "";
  const res = await apiFetch(`${BASE}${query}`);
  if (!res.ok) throw new Error("Failed to fetch route templates");
  return res.json();
}

export async function getRouteTemplateById(templateId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.includeInactiveRoutes != null)
    params.set("include_inactive_routes", filters.includeInactiveRoutes);
  if (filters.includeInactiveRooms != null)
    params.set("include_inactive_rooms", filters.includeInactiveRooms);
  const query = params.toString() ? `?${params}` : "";
  const res = await apiFetch(`${BASE}/${templateId}${query}`);
  if (!res.ok) throw new Error("Failed to fetch route template");
  return res.json();
}

export async function getAvailableRooms(templateId) {
  const res = await apiFetch(`${BASE}/${templateId}/available-rooms`);
  if (!res.ok) throw new Error("Failed to fetch available rooms");
  return res.json();
}

export async function createRouteTemplate(payload) {
  const res = await apiFetch(BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to create route template");
  }
  return res.json();
}

export async function createRoute(templateId, payload, options = {}) {
  const res = await apiFetch(`${BASE}/${templateId}/routes`, {
    method: "POST",
    body: JSON.stringify(withDefer(payload, options)),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to create route");
  }
  return res.json();
}

export async function replaceRouteRooms(routeId, rooms, options = {}) {
  const res = await apiFetch(`${BASE}/routes/${routeId}/rooms`, {
    method: "PUT",
    body: JSON.stringify(withDefer({ rooms }, options)),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update route rooms");
  }
  return res.json();
}

export async function updateRoute(routeId, payload, options = {}) {
  const res = await apiFetch(`${BASE}/routes/${routeId}`, {
    method: "PATCH",
    body: JSON.stringify(withDefer(payload, options)),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update route");
  }
  return res.json();
}

export async function updateRouteRoom(roomMappingId, payload, options = {}) {
  const res = await apiFetch(`${BASE}/rooms/${roomMappingId}`, {
    method: "PATCH",
    body: JSON.stringify(withDefer(payload, options)),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update route room");
  }
  return res.json();
}

export async function getMasterRouteRooms(templateId) {
  const res = await apiFetch(`${BASE}/${templateId}/master-rooms`);
  if (!res.ok) throw new Error("Failed to fetch master route rooms");
  return res.json();
}

export async function reorderMasterRouteRooms(templateId, roomMappingIds) {
  const res = await apiFetch(`${BASE}/${templateId}/master-rooms/order`, {
    method: "PUT",
    body: JSON.stringify({ roomMappingIds }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to reorder master route rooms");
  }
  return res.json();
}

export async function syncRouteTemplateFutureJobs(templateId, payload = {}) {
  const res = await apiFetch(`${BASE}/${templateId}/sync-future-jobs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || "Failed to sync future jobs for route template",
    );
  }

  return res.json();
}

/**
 * Rebuild the template's assignment periods to the current route order
 * (and master order for periods holding several routes), from a date.
 *
 * payload: { effective_from_date, lookahead_days, confirm_period_ids, dry_run }
 * Returns { period_rebuild: { success, result } } where
 * result.requires_confirmation lists manually edited periods that were left
 * alone; send their ids back in confirm_period_ids to process them.
 */
export async function rebuildRoutePeriods(templateId, payload = {}) {
  const res = await apiFetch(`${BASE}/${templateId}/rebuild-periods`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseOrThrow(res, "Failed to rebuild assignment periods");
}

/**
 * Read-only: where do the template's assignment periods (from a date) no
 * longer match the current route order?
 */
export async function getRoutePeriodConsistency(templateId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.effectiveFromDate) {
    params.set("effective_from_date", filters.effectiveFromDate);
  }
  if (filters.lookaheadDays) {
    params.set("lookahead_days", filters.lookaheadDays);
  }
  const query = params.toString() ? `?${params}` : "";
  const res = await apiFetch(`${BASE}/${templateId}/period-consistency${query}`);
  return parseOrThrow(res, "Failed to check assignment periods");
}
